import { createSelector } from '@reduxjs/toolkit'
import { config } from '../../config'

export const selectEvervaultConfig = createSelector(
  state => state.transactionDetails?.isEvervaultEnabled,
  state => state.transactionDetails?.evervault,
  (isEvervaultEnabled, ev) => {
    const data = ev ?? {}
    return {
      // `isEvervaultEnabled` comes straight from the /details response
      enabled: isEvervaultEnabled === true,
      teamId: data.teamId || config.evervaultTeamId || null,
      appId: data.appId || config.evervaultAppId || null,
    }
  }
)

/**
 * True when the Evervault form should be used: enabled by `/details`, both
 * ids resolved, and the form hasn't crashed at runtime (the error boundary sets
 * `pageState.isEvervaultFailed`, so we drop to the next form type).
 */
export const selectIsEvervaultEnabled = state => {
  const { enabled, teamId, appId } = selectEvervaultConfig(state)
  return Boolean(
    enabled && teamId && appId && !state.pageState?.isEvervaultFailed,
  )
}

/**
 * The three card form variants, in priority order:
 * Evervault > PCI iframe > default (manual) form.
 */
export const CARD_FORM_TYPES = {
  evervault: 'evervault',
  pci: 'pci',
  default: 'default',
}

/**
 * Resolve which card form is active.
 * - Evervault enabled (wins even if `isPciEnabled` is also true) -> evervault
 * - otherwise `isPciEnabled` -> pci
 * - otherwise -> default
 */
export const selectCardFormType = state => {
  if (selectIsEvervaultEnabled(state)) return CARD_FORM_TYPES.evervault
  if (state.transactionDetails?.isPciEnabled) return CARD_FORM_TYPES.pci
  return CARD_FORM_TYPES.default
}

/**
 * Use this instead of reading `transactionDetails.isPciEnabled` directly:
 * it is `false` whenever Evervault takes priority.
 */
export const selectIsPciFormEnabled = state =>
  selectCardFormType(state) === CARD_FORM_TYPES.pci

/**
 * Last Evervault `<Card>` onChange payload.
 * @param {Object} state redux root state
 * @returns {Object}
 */
export const selectCardFormValues = state => state.payment.cardFormValues

/**
 * Whether the Evervault card form is complete and valid.
 * @param {Object} state redux root state
 * @returns {boolean}
 */
export const selectIsCardFormComplete = state =>
  state.payment.isCardFormComplete

/**
 * Whether the Evervault card form currently has an error.
 * @param {Object} state redux root state
 * @returns {boolean}
 */
export const selectHasCardError = state => state.payment.hasCardError
